import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Player {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  auth_user_id?: string;
  group_id?: string;
}

interface EditPlayerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player: Player;
  onPlayerUpdated: () => void;
}

export const EditPlayerDialog = ({ open, onOpenChange, player, onPlayerUpdated }: EditPlayerDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLinkedPlayer, setIsLinkedPlayer] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (player) {
      setName(player.name);
      setEmail(player.email || "");
      setPhone(player.phone || "");
      setIsLinkedPlayer(!!player.auth_user_id);
    }
  }, [player]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // For unlinked players, require name. For linked players, allow empty updates.
    if (!isLinkedPlayer && !name.trim()) return;

    setLoading(true);
    try {
      // Check if email is being added (was empty, now has value)
      const isAddingEmail = !player.email && email.trim();
      const isChangingEmail = player.email && email.trim() && player.email !== email.trim();
      
      // Check for duplicates if email is being changed/added
      if ((isAddingEmail || isChangingEmail) && email.trim() && !isLinkedPlayer) {
        const { data: existingPlayers, error: checkError } = await supabase.rpc('find_existing_player_in_group', {
          p_group_id: player.group_id,
          p_email: email.trim(),
          p_name: null
        });

        if (checkError) {
          console.error('Error checking for existing players:', checkError);
        }

        // Check if there's another player with this email
        const duplicatePlayer = existingPlayers?.find(p => 
          p.player_id !== player.id && 
          p.player_email && 
          p.player_email.toLowerCase() === email.trim().toLowerCase()
        );

        if (duplicatePlayer) {
          if (!duplicatePlayer.has_activity) {
            // Safe to delete the duplicate player
            await supabase.from('players').delete().eq('id', duplicatePlayer.player_id);
            toast({
              title: "Duplicate Removed",
              description: "Removed duplicate player and updated this one."
            });
          } else {
            toast({
              title: "Duplicate Player Found",
              description: `Another player with email ${email.trim()} already exists in this group with game history.`,
              variant: "destructive"
            });
            return;
          }
        }
      }

      const updateData: any = {};
      
      // Only update name and phone if player is not linked to authenticated user
      if (!isLinkedPlayer) {
        updateData.name = name.trim();
        updateData.phone = phone.trim() || null;
        updateData.email = email.trim() || null;
      } else {
        // For linked players, don't update name or phone as they're managed through profiles
        // Email is already handled as readonly in the UI
      }
      
      const { error } = await supabase
        .from('players')
        .update(updateData)
        .eq('id', player.id);

      if (error) throw error;

      // Send invitation email if email is being added or changed for unlinked player
      if ((isAddingEmail || isChangingEmail) && !isLinkedPlayer && email.trim()) {
        try {
          const { error: inviteError } = await supabase.functions.invoke('invite-player', {
            body: {
              email: email.trim(),
              redirectTo: 'https://splitpot.lovable.app/?invite=1'
            }
          });
          
          if (inviteError) {
            console.error('Failed to send invitation:', inviteError);
          }
          
          toast({
            title: "Success",
            description: "Player updated and invitation sent successfully"
          });
        } catch (inviteError) {
          console.error('Failed to send invitation:', inviteError);
          toast({
            title: "Partial Success",
            description: "Player updated but failed to send invitation email"
          });
        }
      } else {
        toast({
          title: "Success",
          description: "Player updated successfully"
        });
      }

      onOpenChange(false);
      onPlayerUpdated();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update player",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Player</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Player name"
              required
              readOnly={isLinkedPlayer}
              disabled={isLinkedPlayer}
              className={isLinkedPlayer ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}
            />
            {isLinkedPlayer && (
              <p className="text-xs text-muted-foreground mt-1">
                This player is linked to an authenticated account - name can only be changed through their profile
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="player@example.com"
              readOnly={isLinkedPlayer}
              disabled={isLinkedPlayer}
              className={isLinkedPlayer ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}
            />
            {isLinkedPlayer && (
              <p className="text-xs text-muted-foreground mt-1">
                This player is linked to an authenticated account - email cannot be changed
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1234567890"
              readOnly={isLinkedPlayer}
              disabled={isLinkedPlayer}
              className={isLinkedPlayer ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}
            />
            {isLinkedPlayer && (
              <p className="text-xs text-muted-foreground mt-1">
                This player is linked to an authenticated account - phone can only be changed through their profile
              </p>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || (!isLinkedPlayer && !name.trim())}>
              {loading ? "Updating..." : "Update Player"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};